/** Business-problem copy for visualization info (i) modals */
export const vizNarratives = {
  modelOverview: `📊 Model Overview - Train/Validation/Forecast Analysis

Business Problem: "Can we trust this model to predict future performance?"

What it Solves:
• Model Accuracy Validation: Train MAPE on the first 90% of historical weeks; Validation MAPE on the last 10% (chronological holdout, not random)
• Walk-forward expanding-window CV is used for conformal forecast bands
• Forecast Confidence: Confidence intervals quantify uncertainty on forward PT forecasts
• Investment Confidence: Before shifting large budgets, stakeholders need evidence that forecasts are reliable
• Risk Assessment: Confidence bands show the range of plausible outcomes for planning scenarios

Key Insight: Lower MAPE = more accurate predictions = higher confidence in budget reallocation decisions`,

  channelAttribution: `📊 PT Contribution by Channel

Business Problem: "Which marketing channels are driving PTs, and at what efficiency?"

What it Solves:
• Cost-per-PT Transparency: Bar chart ranks channels by $ cost per actual PT for the selected quarter
• Efficiency Comparison: Quickly spot expensive vs. efficient channels across the 9-channel taxonomy
• Budget Defense: Marketing can justify spend with concrete cost-per-result metrics by channel and motion
• Underperformer Identification: High cost-per-PT channels are candidates for reallocation or review
• Carryover Context: Ad effect duration (half-life) shows how long impact persists after spend stops

Key Insight: Reallocate from high cost-per-PT channels toward efficient channels with headroom before increasing total spend`,

  budgetOptimization: `📊 Budget Optimization Recommendations

Business Problem: "How should I reallocate this quarter's marketing budget to maximize PTs?"

What it Solves:
• Budget Reallocation Strategy: Current vs. recommended spend by channel for the selected region and quarter
• Incremental Impact: Expected incremental PTs and cost/PT per channel from the MMM optimizer
• Saturation Awareness: Saturation % and headroom show where extra spend still returns efficiently
• Incremental Budget: Extra dollars you are considering. It does not rewrite channel recommended budgets
• Interactive Planning: Edit one recommended budget and the others stay put. The Recommended Budget total is the sum of the cells. Calculate (or the refresh icon) refreshes PTs, cost/PT, saturation, and headroom for the channel you changed

Business Value: Answers "If I move budget from Channel A to Channel B, how many more PTs will I generate?"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 COLUMN DEFINITIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📊 Total Budget
What it is: Quarterly budget from the media plan for each channel (region-level total)
Calculation: Allocated across channels using recent spend trends where applicable

💰 Total Spend
What it is: Spend in the current quarter to date for that channel/region
Calculation: Sum of actual spend from historical data
Meaning: How much has already been deployed

💵 Remaining Budget
What it is: Budget still available this quarter
Calculation: Total Budget − Total Spend

📈 % Budget Remaining
What it is: Share of budget not yet spent
Calculation: (Remaining Budget / Total Budget) × 100

🎯 Recommended Budget
What it is: MMM-recommended spend to maximize incremental PTs for the fixed regional total
Calculation: Greedy optimization across Google Brand, Google Non-Brand, Bing Brand, Bing Non-Brand, LinkedIn, Meta, Display, YouTube, and All Channels
Special feature: Editable — change values to see impact on PTs, cost/PT, and saturation

📊 Expected Incremental PTs
What it is: Additional PTs attributed to this channel (leave-one-out vs. full model)
Meaning: Incremental lift from this channel at the recommended spend level

💲 Cost/PT
What it is: Cost per incremental PT
Calculation: Recommended Budget ÷ Expected Incremental PTs (lower is better)

🎨 Saturation %
What it is: How utilized the channel is at the recommended spend level
Meaning:
  • 0–50%: Underutilized — room to scale efficiently
  • 50–70%: Well utilized
  • 70–85%: Diminishing returns emerging
  • 85%+: Highly saturated — marginal gains are smaller

🚀 Headroom
What it is: Additional budget before reaching 85% saturation this quarter
Calculation: Spend to reach 85% saturation − (Recommended Budget + Total Spend)

Key Insight: Optimize within the regional budget cap first; only increase total spend where saturation shows headroom`,

  channelContribution: `📊 Channel Contribution Over Time

Business Problem: "What is driving our PTs — organic baseline, paid media, seasonality, or macro headwinds?"

What it Solves:
• Baseline vs. Media: Separates organic baseline from incremental paid media contribution
• LinkedIn Organic: Combined organic signal from engagements, views, and follows
• Macro Story: Inflation and unemployment bands show external drag on PTs over time
• Trend Analysis: Highlights whether media or macro factors are strengthening or weakening
• Planning Context: Supports seasonal and macro-aware budget conversations

Key Insight: When macro headwinds widen, paid media must work harder to hold total PTs flat`,

  correlationMatrix: `📊 Correlation Matrix with PT Count

Business Problem: "Which channels and macro factors move together with PT outcomes?"

What it Solves:
• Synergy Detection: Positive correlations suggest complementary channels; negative may indicate overlap
• Portfolio Planning: Informs integrated campaigns across correlated channels
• Macro Sensitivity: Shows how unemployment, CPI, and purchase index relate to PTs
• Mix Decisions: Helps avoid over-indexing on redundant spend patterns

Example: If Meta and LinkedIn correlate positively with PTs, coordinated B2B/social flights may compound impact; strong negative pairs may signal cannibalization.

Key Insight: Correlation is not causation — use with MMM incremental metrics before cutting or scaling channels`,

  quarterlyBudgetPlanner: `📊 Forward Quarter Planner - Multi-Quarter Allocation

Business Problem: "How should I pace budget across upcoming quarters and channels to maximize PTs?"

What it Solves:
• Multi-Quarter Plan: Current media plan vs. model-optimal allocation by quarter (Google Brand, Google Non-Brand, Bing Brand, Bing Non-Brand, LinkedIn, Meta, Display, YouTube, All Channels)
• Regional Views: Global, APAC, EMEA, India, LATAM, and North America plans from the same optimizer
• What-If Edits: Change current $ cells, then click Calculate to refresh PT forecasts and optimal split
• Incremental Budget: Add extra dollars on top of the current plan total; Calculate re-optimizes Optimal only at current + incremental (Current $ unchanged)
• Uplift Summary: Stat cards compare total current vs. optimal PTs and budget for the planning horizon

Views:
• Current Plan — your editable quarterly budgets and modeled PTs
• Optimal Allocation — MMM-recommended spend and PTs by quarter
• Compare — side-by-side current vs. optimal with PT delta

Key Insight: Same total budget, reallocated by quarter and channel, can increase PTs without raising spend. Incremental budget models what extra spend could unlock.`,
};
